import * as React from 'react'
import { offeredServieI18nAllowedCustomerKind } from '../../../../services/models/offered-services.service'
import { userForgotPassword, userSignIn, userSignOut } from '../../../../services/models/users.service'
import { customerSearchFuzzyForNewCasting, customerSignUp } from '../../../../services/models/customers.service'
import { StoreProps } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import AsyncButtonComponent from '../../../shared/async-button.component'
import ButtonGroupComponent from '../../../shared/button-group.component'
import ErrorBoxComponent from '../../../shared/error-box.component'
import AvatarComponent from '../../../shared/avatar.component'
import CastingBackofficeCustomerPickerComponent from '../backoffice-customer/casting-backoffice-customer-picker.component'
import SelectableListComponent from '../../../shared/selectable-list.component'
import { modalOpenConfirm } from '../../../../services/utils/modals.service'
import { trackingOnLogin, trackingOnSignup } from '../../../../services/utils/tracking.service'
import InputPasswordComponent from '../../../shared/input-password.component'

enum TabType {
  CUSTOMER_REGISTER,
  CUSTOMER_LOGIN,
  OPERATOR_CREATE,
  OPERATOR_SELECT,
  EMPLOYEE_ALREADY_WORKED_FOR,
  EMPLOYEE_NEVER_WORKED_FOR
}

type Props = StoreProps & {
  submitButtonText: string
  children?: React.ReactNode | React.ReactNode[]
  onIdentityResolved: () => Promise<void>
}

// properties are written in snake_case to match name of API
interface State {
  forgotPassword: boolean
  showPasswordRevealButton: boolean
  email: string
  password: string
  password_confirmation: string
  corporate_name: string
  first_name: string
  last_name: string
  phone: string
  isCorporate: boolean
  tabIndex: number
  accept_cgu: boolean
  accept_gdpr_campaigns: boolean
}

const i18nScoped = new I18nService(require('./casting-customer.component.yml'))

export default class CastingCustomerComponent extends React.Component<Props, State> {
  state: State = {
    forgotPassword: false,
    showPasswordRevealButton: true,
    email: this.props.data.invitation?.email ?? '',
    password: '',
    password_confirmation: '',
    corporate_name: '',
    first_name: this.props.data.invitation?.first_name ?? '',
    last_name: this.props.data.invitation?.last_name ?? '',
    phone: '',
    isCorporate: this.props.data.offeredService?.allowed_customer_kinds[0] === 'corporate',
    tabIndex: 0,
    accept_cgu: false,
    accept_gdpr_campaigns: false
  }

  componentDidUpdate () {
    const { props: { data: { offeredService: os } }, state: { isCorporate } } = this
    
    if (os?.allowed_customer_kinds.length === 1 && isCorporate !== (os.allowed_customer_kinds[0] === 'corporate')) {
      this.setState({ isCorporate: os.allowed_customer_kinds[0] === 'corporate' })
    }
  }

  private canAcceptGDPR () {
    const { customer, market } = this.props.data
    if (!market['gdpr_customer?'] || !market.gdpr_campaign_for_customer.description_accept) return false
    if ((window.currentUser && window.currentUser.type !== 'Customer')) return false
    return this.getCurrentTabType() === TabType.CUSTOMER_REGISTER || customer?.['did_not_accept_gdpr?']
  }

  private async submit (currentTabType = this.getCurrentTabType()): Promise<boolean> {
    const { store, onIdentityResolved, data: { market, customer, invitationToken, referrerCustomerToken, customerOriginToken } } = this.props
    const { email, password, forgotPassword, isCorporate, corporate_name, first_name, last_name, password_confirmation, phone, accept_gdpr_campaigns } = this.state

    store.set({ errors: null, hasAcceptedGdprCampaigns: accept_gdpr_campaigns })
    const actingAsCustomer = !window.currentUser || window.currentUser.type === 'Customer'

    if (customer) {
      var errors: (keyof State | 'customer')[] = []
      var resolver = () => Promise.resolve(customer)
    } else if (currentTabType === TabType.CUSTOMER_LOGIN && !forgotPassword) { 
      errors = ['email', 'password']

      resolver = () => userSignIn({ email, password }).then(res => {
        trackingOnLogin()
        return res.customer
      })
    } else if (currentTabType === TabType.CUSTOMER_REGISTER || currentTabType == TabType.OPERATOR_CREATE) {
      errors = ['email']
      if (!window.currentUser || window.currentUser.type === "Customer") errors.push('password', 'password_confirmation')
      if (market.terms_of_sale_url && (!window.currentUser || window.currentUser.type === 'Customer')) errors.push('accept_cgu')
      if (isCorporate) errors.push('corporate_name')
      if (!isCorporate) errors.push('first_name')
      if (market.preferences['require_last_name?']) errors.push('last_name')
      if (market.preferences['phone_required?']) errors.push('phone')

      const body = {
        email,
        corporate_name,
        first_name,
        last_name,
        password,
        password_confirmation,
        phone_numbers_attributes: phone ? [{ phone_number: phone }] : [],
        locale: window.locale,
        token: invitationToken,
        referrer_customer: referrerCustomerToken,
        source: customerOriginToken
      }

      resolver = () => customerSignUp(body).then(res => {
        trackingOnSignup()
        return res.customer
      })
    } else if (currentTabType === TabType.OPERATOR_SELECT || currentTabType === TabType.EMPLOYEE_ALREADY_WORKED_FOR) {
      errors = ['customer']
      resolver = () => Promise.resolve(customer)
    } else if (currentTabType === TabType.EMPLOYEE_NEVER_WORKED_FOR) {
      errors = ['email', 'first_name', 'last_name', 'phone']
      if (isCorporate) errors.push('corporate_name')

      const body = { email, corporate_name, first_name, last_name, phone_number: phone, phone_numbers_attributes: phone ? [{ phone_number: phone }] : [], locale: window.locale }
      resolver = () => customerSearchFuzzyForNewCasting(body, 'index_casting_form').then(async customers => {
        if (customers.length === 1) return customers[0]
        if (customers.length > 1) throw { full_messages: [i18nScoped.t('multiple_customers_matches')] }
        
        const confirmAccountCreation = await modalOpenConfirm(i18nScoped.t('unable_to_find_the_customer'), i18nScoped.t('do_you_want_to_register_the_customer'))
        if (confirmAccountCreation) return customerSignUp(body).then(res => res.customer)

        // throw an empty error in case of cancellation, that will not show any warning in the UI
        throw { full_messages: [] }
      })
    }
    
    const effectiveErrors = errors
      .filter(key => key === 'customer' ? !customer : !this.state[key])
      .map(key => ({ key, msg: `${actingAsCustomer ? 'customer' : 'not_customer'}.undefined_${key}` }))

    if (errors.includes('password_confirmation') && password !== password_confirmation) {
      effectiveErrors.push({ key: 'password_confirmation', msg: 'invalid_password_confirmation' })
    }

    if (errors.includes('phone') && !/^\+?([(+]*\d+[)\s.-]?\s?){6,}$/.test(phone)) {
      effectiveErrors.push({ key: 'phone', msg: `${actingAsCustomer ? 'customer' : 'not_customer'}.invalid_phone` })
    }

    if (effectiveErrors.length) {
      store.setError({
        errors: effectiveErrors.reduce((obj, err) => ({ ...obj, [err.key]: (obj[err.key] || []).concat(err.msg) }), {} as any),
        full_messages: effectiveErrors.map(err => i18nScoped.t(err.msg))
      })

      return false
    }

    // prevents the browser from remembering the password as an autocomplete suggestion before sending the form by setting showRevealButton to false
    // (https://www.makethingsaccessible.com/guides/make-an-accessible-password-reveal-input/)
    this.setState({ showPasswordRevealButton: false })

    if (!customer && currentTabType === TabType.OPERATOR_CREATE) {
      // do not call callback when supplier registers a new customer, so he can fill service_delivery address
      return resolver().then(customer => store.set({ customer })).then(() => false).catch(err => { store.setError(err); return false })
    }

    return resolver()
      .then(customer => store.set({ customer }))
      .then(() => !customer && store.checkOfferedServiceAdjustmentOnceCustomerIsKnown())
      .then(onIdentityResolved)
      .then(() => true)
      .catch(err => { 
        store.setError(err)
        this.setState({ showPasswordRevealButton: true })
        return false
      })
  }

  private async logout () {
    await userSignOut()
    this.props.store.set({ customer: null })
  }

  private async sendResetPasswordLink () {
    const { props: { store }, state: { email } } = this
    if (!email) return store.setError({ errors: { email: [] }, full_messages: [i18nScoped.t('customer.undefined_email')] })
    store.set({ errors: null })

    return userForgotPassword({ email })
      .then(() => this.setState({ forgotPassword: false }))
      .catch(err => store.setError(err instanceof Error ? err : { ...err, full_messages: [i18nScoped.t('email_not_found')] }))
  }

  private getCurrentTabType () {
    return this.getTabs()[this.state.tabIndex]?.type
  }

  private getTabs () {
    const userType = window.currentUser?.type
    const tabs: { type: TabType, title: string, content: () => React.ReactNode }[] = []

    if (userType === 'Employee') {
      tabs.push({ type: TabType.EMPLOYEE_ALREADY_WORKED_FOR, title: i18nScoped.t('my_customer'), content: () => this.renderSelectExistingCustomerForm() })
      tabs.push({ type: TabType.EMPLOYEE_NEVER_WORKED_FOR, title: i18nScoped.t('not_my_customer'), content: () => this.renderEmployeeNeverWorkedFromTheCustomer() })
    } else if (userType === 'Operator') {
      tabs.push({ type: TabType.OPERATOR_CREATE, title: i18nScoped.t('create_account_for_customer'), content: () => this.renderSignupForm() })
      tabs.push({ type: TabType.OPERATOR_SELECT, title: i18nScoped.t('select_existing_customer'), content: () => this.renderSelectExistingCustomerForm() })
    } else {
      tabs.push({ type: TabType.CUSTOMER_REGISTER, title: i18nScoped.t('sign_up'), content: () => this.renderSignupForm() })
      tabs.push({ type: TabType.CUSTOMER_LOGIN, title: i18nScoped.t('sign_in'), content: () => this.renderSigninForm() })
    }

    return tabs
  }

  private renderInput ({ type, placeholder, field, required }: { type?: string, placeholder: string, field: { [K in keyof State]: State[K] extends string ? K : never }[keyof State], required?: boolean }){
    const commonProps: JSX.IntrinsicElements['input'] = {
      id: `js-test-${field}`,
      value: this.state[field] ?? '',
      placeholder: i18nScoped.t(placeholder) + (required ? '*' : ' (' + i18nScoped.t('optional') + ')'),
      onChange: evt => this.setState({ [field]: evt.target.value } as any),
      className: this.props.store.hasError(field) ? 'has-errors' : '',
      required
    }

    if (type === 'password') return <InputPasswordComponent { ...commonProps } showRevealButton={ this.state.showPasswordRevealButton } />
    return <input type={ type || 'text' } { ...commonProps } />
  }

  private renderCurrentCustomer () {
    const { data: { customer }, children } = this.props

    return <React.Fragment>
      <div className="castingUser" key="logged_account">
        <AvatarComponent src={ customer.photo.url } initials={ customer.initial } />
        <div className="castingUser-name">{ customer.full_name }</div>
        <div className="castingUser-email">{ customer.email }</div>
        { !window.currentUser?.role && <a onClick={ () => this.logout() } className="castingUser-logout">{ i18nScoped.t('logout') }</a> }
      </div>

      { this.canAcceptGDPR() && this.renderGDPR() }
      { children }
    </React.Fragment>
  }

  private renderSignupForm () {
    const { props: { data: { offeredService, market } }, state: { isCorporate } } = this
    const actingAsCustomer = !window.currentUser || window.currentUser.type === 'Customer'

    return <React.Fragment>
      { offeredService?.allowed_customer_kinds.length === 2 && <ButtonGroupComponent
        name="isCorporate"
        value={ isCorporate }
        onChange={ val => this.setState({ isCorporate: val }) }
        data={ [
          { text: offeredServieI18nAllowedCustomerKind('individual'), value: false, disabled: false },
          { text: offeredServieI18nAllowedCustomerKind('corporate'), value: true, disabled: false }
        ] } /> }

        { isCorporate && this.renderInput({ placeholder: 'corporate_name', field: 'corporate_name', required: true }) }
        { this.renderInput({ placeholder: isCorporate ? 'contact_first_name' : 'first_name', field: 'first_name', required: !isCorporate }) }
        { (market.preferences['require_last_name?'] || !actingAsCustomer) && this.renderInput({ placeholder: isCorporate ? 'contact_last_name' : 'last_name', field: 'last_name', required: !isCorporate }) }
        { this.renderInput({ type: 'email', placeholder: 'email', field: 'email', required: true }) }
        { this.renderInput({ type: 'tel', placeholder: 'phone', field: 'phone', required: market.preferences['phone_required?'] }) }
        { actingAsCustomer && this.renderInput({ type: 'password', placeholder: 'password_sign_up', field: 'password', required: true }) }
        { actingAsCustomer && this.renderInput({ type: 'password', placeholder: 'password_confirmation', field: 'password_confirmation', required: true }) }

        { market.terms_of_sale_url && actingAsCustomer && <div className={ `inputCheckbox ${this.props.store.hasError('accept_cgu') ? 'has-errors' : ''}` }>
          <input id="accept_cgu" type="checkbox" checked={ this.state.accept_cgu } onChange={ event => this.setState({ accept_cgu: event.target.checked }) } />
          <label htmlFor="accept_cgu" dangerouslySetInnerHTML={{ __html: '<span>' + i18nScoped.t('i_agree_terms_of_sale_html', { url: market.terms_of_sale_url }) + '</span>' }} />
        </div> }

        { this.canAcceptGDPR() && this.renderGDPR() }
      </React.Fragment>
  }

  private renderSigninForm () {
    const { forgotPassword } = this.state

    return <React.Fragment>
      { !forgotPassword && <React.Fragment>
        { this.renderInput({ type: 'email', placeholder: 'email', field: 'email', required: true }) }
        { this.renderInput({ type: 'password', placeholder: 'password_sign_in', field: 'password', required: true }) }
        <p><a onClick={ () => this.setState({ forgotPassword: true }) }>{ i18nScoped.t('forgot_password') }</a></p>
        { this.canAcceptGDPR() && this.renderGDPR() }
      </React.Fragment> }

      { forgotPassword && <React.Fragment>
        { this.renderInput({ type: 'email', placeholder: 'email', field: 'email', required: true }) }
        <p><a onClick={ () => this.setState({ forgotPassword: false }) }>{ i18nScoped.t('back_to_sign_in') }</a></p>
      </React.Fragment> }
    </React.Fragment>
  }

  private renderSelectExistingCustomerForm () {
    return <CastingBackofficeCustomerPickerComponent
      store={ this.props.store }
      data={ this.props.data }
      afterSelect={ () => this.props.store.nextFocus({ from: 'end-of-step-2' }) } />
  }

  private renderEmployeeNeverWorkedFromTheCustomer () {
    return <React.Fragment>
      { this.renderSignupForm() }
    </React.Fragment>
  }

  private renderGDPR () {
    return <div className={ `inputCheckbox u-mbs ${this.props.store.hasError('accept_gdpr_campaigns') ? 'has-errors' : ''}` }>
      <input type="checkbox" id="gdpr_customer" onChange={ event => this.setState({ accept_gdpr_campaigns: event.target.checked }) } />
      <label htmlFor="gdpr_customer">
        <div className='tooltip tooltip-container-module tooltip--noIcon tooltip--alternate'>
          <p>{ this.props.store.data.market.gdpr_campaign_for_customer.description_accept }</p>
          <div className='tooltip-inner u-mbn'>{ this.props.data.market.gdpr_campaign_for_customer.description_preferences }</div>
        </div>
      </label>
    </div>
  }

  render () {
    const { props: { data: { errors, customer, market }, submitButtonText }, state: { forgotPassword, tabIndex } } = this
    const renderForgotButton = this.getCurrentTabType() === TabType.CUSTOMER_LOGIN && forgotPassword && !customer
    const tabs = this.getTabs()

    return <div>
      { customer && this.renderCurrentCustomer() }

      { !customer && <React.Fragment>
        <SelectableListComponent
          name="customerPickerStrategy"
          className='u-mbs'
          data={ tabs }
          value={ tabs[tabIndex] }
          onChange={ tab => this.setState({ tabIndex: tabs.indexOf(tab) }) }
          formatter={ tab => tab.title } />

        { tabs[tabIndex]?.content() }
      </React.Fragment> }

      <ErrorBoxComponent fullMessages={ errors?.full_messages || [] } />

      { !renderForgotButton && <AsyncButtonComponent
        className="button button--block button--main u-mtm"
        onClick={ () => this.submit() }
        onWait="prefix"
        singleClick
        children={ !customer && this.getCurrentTabType() === TabType.OPERATOR_CREATE ? i18nScoped.t('create_account_for_customer_button') : submitButtonText } /> }

      { renderForgotButton && <AsyncButtonComponent
        className="buttonNeutral button--block button--narrow u-mtm"
        onClick={ () => this.sendResetPasswordLink() }
        onWait="prefix"
        children={ i18nScoped.t('send_reset_password_link') } /> }

      { market.cancel_conditions_formatted && <p id="cancel_condition_formatted">{ market.cancel_conditions_formatted }</p> }
    </div>
  }
}
